import type { Sealed } from './crypto';

/**
 * The local database of the offline sync (ADR-0013): one IndexedDB per account and company,
 * contents encrypted. Plain are only IDs, entity names, the state of a change or upload, the size
 * and type of a file and the device key as non-extractable `CryptoKey`.
 */

const PREFIX = 'staw-offline-';
/** 2: files on the device and the upload queue (ADR-0034, Nachtrag). */
const VERSION = 2;
/** Names of the databases this browser holds, so sign-out finds them all. */
const REGISTRY_KEY = 'staw.offline.databases';
/** Account and company of the last sync, for a start without network. */
const LAST_KEY = 'staw.offline.last';

export interface StoredRecord {
  entity: string;
  id: string;
  sealed: Sealed;
}

export type ChangeState = 'pending' | 'conflict' | 'rejected';

export interface StoredChange {
  /** Queue order, assigned by IndexedDB. */
  seq?: number;
  id: string;
  entity: string;
  recordId: string;
  state: ChangeState;
  /** Why the server refused it, e.g. `forbidden`. */
  reason?: string;
  /** The change itself; `null` once it expired on the device. */
  sealed: Sealed | null;
}

/** A file on the device: downloaded for a prepared record, or waiting for its upload. */
export interface StoredBlob {
  fileId: string;
  size: number;
  type: string;
  /** The bytes. */
  sealed: Sealed;
}

export type UploadState = 'waiting' | 'failed';

export interface StoredUpload {
  fileId: string;
  entity: string;
  recordId: string;
  state: UploadState;
  /** Why the last attempt failed, e.g. `file-too-large`. */
  error?: string;
  /** Queue order. */
  order: number;
  /** Name, type, size, dependencies and time; `null` once it expired on the device. */
  sealed: Sealed | null;
}

export interface Identity {
  accountId: string;
  companyId: string;
}

export function databaseName({ accountId, companyId }: Identity): string {
  return `${PREFIX}${accountId}-${companyId}`;
}

function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB request failed'));
  });
}

function finished(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onabort = tx.onerror = () => reject(tx.error ?? new Error('IndexedDB transaction failed'));
  });
}

function readList(key: string): string[] {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(key) ?? '[]');
    return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

function writeStorage(key: string, value: unknown): void {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Without storage the offline start and the clean-up at sign-out cover less.
  }
}

export function knownDatabase(identity: Identity): boolean {
  return readList(REGISTRY_KEY).includes(databaseName(identity));
}

export function lastIdentity(): Identity | null {
  try {
    const value = JSON.parse(localStorage.getItem(LAST_KEY) ?? 'null') as Partial<Identity> | null;
    return typeof value?.accountId === 'string' && typeof value.companyId === 'string'
      ? { accountId: value.accountId, companyId: value.companyId }
      : null;
  } catch {
    return null;
  }
}

export function rememberIdentity(identity: Identity | null): void {
  writeStorage(LAST_KEY, identity);
}

export class OfflineStore {
  private constructor(
    private readonly db: IDBDatabase,
    readonly name: string,
  ) {}

  static async open(identity: Identity): Promise<OfflineStore> {
    const name = databaseName(identity);
    const opening = indexedDB.open(name, VERSION);
    opening.onupgradeneeded = (event) => {
      const db = opening.result;
      if (event.oldVersion < 1) {
        db.createObjectStore('records', { keyPath: ['entity', 'id'] }).createIndex(
          'entity',
          'entity',
        );
        db.createObjectStore('changes', { keyPath: 'seq', autoIncrement: true }).createIndex(
          'entity',
          'entity',
        );
        db.createObjectStore('meta', { keyPath: 'key' });
      }
      if (event.oldVersion < 2) {
        // The index lets the app add up the sizes without reading the files.
        db.createObjectStore('blobs', { keyPath: 'fileId' }).createIndex('size', 'size');
        db.createObjectStore('uploads', { keyPath: 'fileId' });
      }
    };
    const db = await request(opening);
    // Another tab upgrading or deleting the database gets it free.
    db.onversionchange = () => db.close();
    const list = readList(REGISTRY_KEY);
    if (!list.includes(name)) writeStorage(REGISTRY_KEY, [...list, name]);
    return new OfflineStore(db, name);
  }

  async meta<T>(key: string): Promise<T | undefined> {
    const tx = this.db.transaction('meta');
    const row = await request(
      tx.objectStore('meta').get(key) as IDBRequest<{ value: T } | undefined>,
    );
    return row?.value;
  }

  async setMeta(key: string, value: unknown): Promise<void> {
    const tx = this.db.transaction('meta', 'readwrite');
    tx.objectStore('meta').put({ key, value });
    await finished(tx);
  }

  async records(): Promise<StoredRecord[]> {
    return request(this.db.transaction('records').objectStore('records').getAll()) as Promise<
      StoredRecord[]
    >;
  }

  async putRecords(rows: readonly StoredRecord[], remove: readonly [string, string][] = []) {
    const tx = this.db.transaction('records', 'readwrite');
    const store = tx.objectStore('records');
    for (const row of rows) store.put(row);
    for (const key of remove) store.delete(key);
    await finished(tx);
  }

  async clearEntity(entity: string): Promise<void> {
    const tx = this.db.transaction(['records', 'changes'], 'readwrite');
    for (const name of ['records', 'changes'] as const) {
      const store = tx.objectStore(name);
      const keys = await request(store.index('entity').getAllKeys(entity));
      for (const key of keys) store.delete(key);
    }
    await finished(tx);
  }

  async changes(): Promise<StoredChange[]> {
    return request(this.db.transaction('changes').objectStore('changes').getAll()) as Promise<
      StoredChange[]
    >;
  }

  async addChange(change: StoredChange): Promise<number> {
    const tx = this.db.transaction('changes', 'readwrite');
    const seq = await request(tx.objectStore('changes').add(change));
    await finished(tx);
    return seq as number;
  }

  async putChanges(rows: readonly StoredChange[], remove: readonly number[] = []): Promise<void> {
    const tx = this.db.transaction('changes', 'readwrite');
    const store = tx.objectStore('changes');
    for (const row of rows) store.put(row);
    for (const seq of remove) store.delete(seq);
    await finished(tx);
  }

  /** Sizes of the files on the device by ID, read from the index. */
  async blobSizes(): Promise<Map<string, number>> {
    const sizes = new Map<string, number>();
    const cursor = this.db.transaction('blobs').objectStore('blobs').index('size').openKeyCursor();
    await new Promise<void>((resolve, reject) => {
      cursor.onsuccess = () => {
        const at = cursor.result;
        if (!at) return resolve();
        sizes.set(at.primaryKey as string, at.key as number);
        at.continue();
      };
      cursor.onerror = () => reject(cursor.error ?? new Error('IndexedDB cursor failed'));
    });
    return sizes;
  }

  blob(fileId: string): Promise<StoredBlob | undefined> {
    return request(
      this.db.transaction('blobs').objectStore('blobs').get(fileId) as IDBRequest<
        StoredBlob | undefined
      >,
    );
  }

  /** A file, with its upload in the same transaction when it waits for one. */
  async putBlob(blob: StoredBlob, upload?: StoredUpload): Promise<void> {
    const tx = this.db.transaction(['blobs', 'uploads'], 'readwrite');
    tx.objectStore('blobs').put(blob);
    if (upload) tx.objectStore('uploads').put(upload);
    await finished(tx);
  }

  async deleteBlobs(fileIds: readonly string[]): Promise<void> {
    const tx = this.db.transaction('blobs', 'readwrite');
    for (const id of fileIds) tx.objectStore('blobs').delete(id);
    await finished(tx);
  }

  async uploads(): Promise<StoredUpload[]> {
    const rows = (await request(
      this.db.transaction('uploads').objectStore('uploads').getAll(),
    )) as StoredUpload[];
    return rows.sort((a, b) => a.order - b.order);
  }

  async putUploads(rows: readonly StoredUpload[], remove: readonly string[] = []): Promise<void> {
    const tx = this.db.transaction('uploads', 'readwrite');
    const store = tx.objectStore('uploads');
    for (const row of rows) store.put(row);
    for (const id of remove) store.delete(id);
    await finished(tx);
  }

  close(): void {
    this.db.close();
  }
}

function deleteDatabase(name: string): Promise<void> {
  return new Promise((resolve) => {
    const req = indexedDB.deleteDatabase(name);
    // Blocked by another open tab: it closes on `versionchange`, the deletion finishes then.
    req.onsuccess = req.onerror = req.onblocked = () => resolve();
  });
}

export async function deleteStore(identity: Identity): Promise<void> {
  const name = databaseName(identity);
  await deleteDatabase(name);
  writeStorage(
    REGISTRY_KEY,
    readList(REGISTRY_KEY).filter((n) => n !== name),
  );
  const last = lastIdentity();
  if (last && databaseName(last) === name) rememberIdentity(null);
}

/** No start without network for this account and company until its next sync with a session. */
export function forgetIdentity(identity: Identity): void {
  const last = lastIdentity();
  if (last && databaseName(last) === databaseName(identity)) rememberIdentity(null);
}

/** Removes every offline database of this browser: at sign-out (ADR-0013). */
export async function deleteAllStores(): Promise<void> {
  const names = new Set(readList(REGISTRY_KEY));
  try {
    for (const db of await indexedDB.databases()) {
      if (db.name?.startsWith(PREFIX)) names.add(db.name);
    }
  } catch {
    // Older browsers cannot list databases; the registry has them.
  }
  await Promise.all([...names].map(deleteDatabase));
  writeStorage(REGISTRY_KEY, null);
  rememberIdentity(null);
}
